import React from "react";
import { Button, Card, Input, Space, Switch } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Explanation,
  ExplanationRow,
} from "../../../services/gameQuiz/gameQuiz.types";
import {
  createEmptyExplanation,
  createEmptyExplanationRow,
} from "./gameTypes";
import OptionRichText from "./OptionRichText";
import ImageUploadInput from "./ImageUploadInput";
import AudioUploadInput from "./AudioUploadInput";

const MAX_ANSWER_LENGTH = 512;

type Props = {
  value?: Explanation;
  onChange: (value: Explanation | undefined) => void;
};

const ExplanationEditor: React.FC<Props> = ({ value, onChange }) => {
  const enabled = !!value;
  const explanation: Explanation = value ?? createEmptyExplanation();

  const toggleEnabled = (next: boolean) => {
    onChange(next ? createEmptyExplanation() : undefined);
  };

  const updateField = <K extends keyof Explanation>(
    key: K,
    val: Explanation[K],
  ) => {
    onChange({ ...explanation, [key]: val });
  };

  const updateRow = (rowId: string, patch: Partial<ExplanationRow>) => {
    onChange({
      ...explanation,
      rows: explanation.rows.map((r) =>
        r.id === rowId ? { ...r, ...patch } : r,
      ),
    });
  };

  const addRow = () => {
    onChange({
      ...explanation,
      rows: [...explanation.rows, createEmptyExplanationRow()],
    });
  };

  const removeRow = (rowId: string) => {
    onChange({
      ...explanation,
      rows: explanation.rows.filter((r) => r.id !== rowId),
    });
  };

  return (
    <Card
      size="small"
      title="Explicación de la respuesta"
      extra={
        <Space size="small">
          <span style={{ fontSize: 12, color: "#888" }}>
            {enabled ? "Activa" : "Desactivada"}
          </span>
          <Switch checked={enabled} onChange={toggleEnabled} />
        </Space>
      }
      style={{ background: "var(--bo-inset-bg)", borderColor: "var(--bo-card-border)" }}
    >
      {!enabled ? (
        <div style={{ color: "#888", fontSize: 13 }}>
          Activá el switch para mostrar una explicación al alumno luego de
          responder.
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <label>Respuesta</label>
            <Input.TextArea
              value={explanation.answer}
              onChange={(e) =>
                updateField(
                  "answer",
                  e.target.value.slice(0, MAX_ANSWER_LENGTH),
                )
              }
              placeholder="El texto se escribirá aquí (máx 512)"
              rows={3}
              showCount
              maxLength={MAX_ANSWER_LENGTH}
            />
          </div>

          <div>
            <label>Audio (opcional)</label>
            <div style={{ marginTop: 4 }}>
              <AudioUploadInput
                value={explanation.audio}
                onChange={(url) => updateField("audio", url)}
                folder="game-quiz/explanations/audio"
                label="Subir audio (MP3, WAV)"
              />
            </div>
          </div>

          <div>
            <label>Editor de contenido</label>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
              Agrega texto e imágenes por filas
            </div>
            <Space
              direction="vertical"
              size="middle"
              style={{ width: "100%" }}
            >
              {explanation.rows.map((row, idx) => (
                <div
                  key={row.id}
                  style={{
                    border: "1px solid var(--bo-card-border)",
                    borderRadius: 8,
                    padding: 12,
                    background: "var(--bo-card-bg)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 12, color: "#888" }}>
                      Fila {idx + 1}
                    </span>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeRow(row.id)}
                      disabled={explanation.rows.length <= 1}
                    />
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 200px",
                      gap: 12,
                      alignItems: "start",
                    }}
                  >
                    <OptionRichText
                      value={row.text || ""}
                      onChange={(html) => updateRow(row.id, { text: html })}
                      placeholder="Texto de esta fila"
                    />
                    <ImageUploadInput
                      value={row.image}
                      onChange={(url) => updateRow(row.id, { image: url })}
                      label="Click para subir imagen"
                      folder="game-quiz/explanations"
                    />
                  </div>
                </div>
              ))}
            </Space>

            <div style={{ marginTop: 12 }}>
              <Button
                icon={<PlusOutlined />}
                onClick={addRow}
                type="dashed"
                block
              >
                Agregar fila
              </Button>
            </div>
          </div>
        </Space>
      )}
    </Card>
  );
};

export default ExplanationEditor;
